"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import type { LandingExam } from "./landing-data";
import { statusOf, StatusDot, StatusLegend } from "@/components/catalog/status";
import { EXAM_CONFIGS, type ExamType } from "@/lib/exam/config";

const CATEGORY_LABELS: Record<LandingExam["category"], string> = {
  academic: "Admissions",
  english: "English proficiency",
  medical: "Medical and nursing",
  professional: "Professional",
  licensing: "Licensing",
  language: "Languages",
};

/** Flagship = live, full-fidelity exams — the front row. */
const FLAGSHIP_ORDER = ["toefl", "ielts", "sat", "act", "gre"];

function flagshipMeta(slug: string): string {
  const cfg = EXAM_CONFIGS[slug as ExamType];
  if (!cfg) return "";
  const sections = cfg.sections.map(s => s.label).join(" · ");
  return sections;
}

interface Props {
  exams: LandingExam[];
  /** Number of practice exams per flagship slug (e.g. { toefl: 99 }). */
  examCounts?: Record<string, number>;
}

export function ExamCatalog({ exams, examCounts = {} }: Props) {
  const [query, setQuery] = useState("");

  const { flagship, betaByCategory, comingSoon } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (e: LandingExam) =>
      !q || e.name.toLowerCase().includes(q) || e.abbr.toLowerCase().includes(q) || e.desc.toLowerCase().includes(q);

    const flagship = FLAGSHIP_ORDER
      .map(slug => exams.find(e => e.slug === slug))
      .filter((e): e is LandingExam => !!e && statusOf(e.slug) === "live" && match(e));

    const betaByCategory = new Map<LandingExam["category"], LandingExam[]>();
    const comingSoon: LandingExam[] = [];
    for (const e of exams) {
      if (FLAGSHIP_ORDER.includes(e.slug)) continue;
      if (!match(e)) continue;
      const st = statusOf(e.slug);
      if (st === "coming_soon") comingSoon.push(e);
      else if (st === "beta" || st === "live") {
        const arr = betaByCategory.get(e.category) ?? [];
        arr.push(e);
        betaByCategory.set(e.category, arr);
      }
    }
    return { flagship, betaByCategory, comingSoon };
  }, [exams, query]);

  const nothing = flagship.length === 0 && betaByCategory.size === 0 && comingSoon.length === 0;

  return (
    <div>
      {/* Search + legend */}
      <div className="flex items-center justify-between gap-4 mb-7 flex-wrap">
        <div className="flex items-center gap-2 bg-white border border-cream rounded-xl px-4 py-2.5 min-w-[240px]">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--text-muted)] opacity-60">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search exams…"
            className="bg-transparent outline-none text-[13px] text-[var(--text-primary)] placeholder-[var(--text-muted)] flex-1 min-w-0"
          />
        </div>
        <StatusLegend />
      </div>

      {nothing && (
        <div className="text-center py-12 text-[var(--text-muted)] text-sm">No exams match your search.</div>
      )}

      {/* Tier 1 — flagship */}
      {flagship.length > 0 && (
        <>
          <div className="text-[12px] font-bold text-emerald-700 mb-3 flex items-center gap-2">
            <StatusDot /> FLAGSHIP — COMPLETE AND EXAM-IDENTICAL
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-10">
            {flagship.map(e => (
              <Link
                key={e.slug}
                href={`/${e.slug}`}
                className="group bg-white border border-cream rounded-xl p-4 text-center transition-all hover:border-indigo-brand hover:shadow-cream"
              >
                <div className="flex items-center justify-center gap-1.5 font-bold text-[15px] tracking-tight">
                  {e.abbr}
                  <StatusDot />
                </div>
                <div className="text-[11px] text-[var(--text-muted)] mt-1 leading-snug min-h-[30px]">
                  {examCounts[e.slug] ? `${examCounts[e.slug]} exams · ` : ""}{e.score}
                  <span className="block truncate" title={flagshipMeta(e.slug)}>{flagshipMeta(e.slug)}</span>
                </div>
                <div className="mt-3 text-[12px] font-semibold text-white bg-indigo-brand rounded-lg py-1.5 group-hover:opacity-90">
                  Start free
                </div>
              </Link>
            ))}
          </div>
        </>
      )}

      {/* Tier 2 — beta, clustered by category */}
      {betaByCategory.size > 0 && (
        <>
          <div className="text-[12px] font-bold text-amber-700 mb-3 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-400" /> IN BETA — FULL RUNS, SOME POLISH PENDING
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
            {[...betaByCategory.entries()].map(([cat, list]) => (
              <div key={cat} className="bg-white border border-cream rounded-xl p-4">
                <div className="text-[11px] text-[var(--text-muted)] font-semibold mb-2.5">{CATEGORY_LABELS[cat]}</div>
                <div className="flex flex-wrap gap-1.5">
                  {list.map(e => (
                    <Link
                      key={e.slug}
                      href={`/${e.slug}`}
                      className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[var(--text-secondary)] bg-cream rounded-full px-3 py-1.5 hover:text-indigo-brand transition-colors"
                      title={e.desc}
                    >
                      {e.abbr}
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Tier 3 — coming soon */}
      {comingSoon.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-[var(--text-muted)] font-semibold mr-1">Coming soon:</span>
          {comingSoon.map(e => (
            <Link
              key={e.slug}
              href={`/${e.slug}`}
              className="inline-flex items-center gap-1.5 text-[12px] text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-full px-3 py-1.5 hover:text-[var(--text-secondary)] transition-colors"
            >
              {e.abbr}
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
